import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer/Footer';
import { processError } from '../../services/ServiceUtils';
import WalletRow from './WalletRow';
import WalletDonut from './WalletDonut';
import EarnPanel from './EarnPanel';
import CapitalPizza from '../Settings/CapitalPizza';
import Toast from '../../components/Toast/Toast';
import { getEmptyMessage, getErrorMessage } from '../../utils/MessageUtils';
import { getFullBalance } from '../../services/ExchangeService';
import { Link } from 'react-router-dom';
import { getCurrentEnvironment, subscribeToCurrentEnvironment } from '../../utils/CurrentEnvironmentStorage';
import { getCurrentMarket, subscribeToCurrentMarket, MARKET_FUTURES } from '../../utils/MarketStorage';
import { getFuturesAccount } from '../../services/FuturesService';
import FuturesAccountCard from '../../components/FuturesAccountCard/FuturesAccountCard';
import NewOrderButton from '../../components/NewOrder/NewOrderButton';
import NewOrderModal from '../../components/NewOrder/NewOrderModal';
import SelectFiat from '../../components/SelectFiat/SelectFiat';
import { setDefaultFiat, getDefaultFiat } from '../../utils/FiatUtils';

function Wallet() {

    const history = useHistory();
    const [balances, setBalances] = useState([]);
    const [total, setTotal] = useState('');
    const [notification, setNotification] = useState(getEmptyMessage());
    const [noCreds, setNoCreds] = useState(false);
    const [fiat, setFiat] = useState(getDefaultFiat());
    const [environment, setEnvironment] = useState(getCurrentEnvironment());
    const [market, setMarket] = useState(getCurrentMarket());
    const [futAccount, setFutAccount] = useState({ positions: [], balance: null });
    const isFutures = market === MARKET_FUTURES;

    useEffect(() => subscribeToCurrentEnvironment(env => {
        setEnvironment(env);
        setNoCreds(false);
        setBalances([]);
    }), []);
    useEffect(() => subscribeToCurrentMarket(m => setMarket(m)), []);

    // Carteira de FUTUROS = conta de margem (USDT-M) + posições abertas (read-only aqui; fechar
    // posição fica na "mesa" em /futures). Reusa getFuturesAccount, igual à tela de Futuros.
    useEffect(() => {
        if (!isFutures) return;
        getFuturesAccount(environment)
            .then(setFutAccount)
            .catch(err => setNotification(getErrorMessage(processError(err))));
    }, [isFutures, environment]);

    useEffect(() => {
        if (isFutures) return; // spot only
        getFullBalance(fiat)
            .then(info => {
                setTotal(info.fiatEstimate);
                delete (info.fiatEstimate);

                const balances = Object.entries(info).map(item => {
                    return {
                        symbol: item[0],
                        available: item[1].available,
                        onOrder: item[1].onOrder,
                        fiatEstimate: item[1].fiatEstimate,
                        avg: item[1].avg
                    }
                });
                setBalances(balances);
            })
            .catch(err => {
                const raw = err.response?.data;
                if (raw?.error === 'NO_PROD_CREDENTIALS' || raw?.error === 'NO_TEST_CREDENTIALS') {
                    setNoCreds(true);
                } else {
                    setNotification(getErrorMessage(processError(err)));
                }
            })
    }, [fiat, environment, isFutures]);

    function onOrderSubmit(event) {
        history.go(0);
    }

    function onFiatChange(event) {
        const selectedFiat = event.target.value;
        setDefaultFiat(selectedFiat);
        setFiat(selectedFiat);
    }

    return (
        <React.Fragment>
            <Menu />
            <main className='content'>
                <div className='d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4'>
                    <div className='d-block mb-4 mb-md-0'>
                        <h2 className='h4'>
                            Carteira
                            <span className={`badge ms-2 ${isFutures ? 'bg-warning text-dark' : 'bg-secondary'}`} style={{ fontSize: '0.5em', verticalAlign: 'middle' }}>
                                {isFutures ? 'Futuros (margem)' : 'Spot'}
                            </span>
                        </h2>
                    </div>
                    {!isFutures && (
                        <div className='btn-toolbar mb-2 mb-md-0'>
                            <div className='d-inline-flex align-items-center'>
                                <div className='me-2'>
                                    <SelectFiat onChange={onFiatChange} />
                                </div>
                                <NewOrderButton />
                            </div>
                        </div>
                    )}
                </div>
                {noCreds && !isFutures && (
                    <div className='alert alert-warning'>
                        Carteira de <strong>{getCurrentEnvironment() === 'test' ? 'testnet' : 'produção'}</strong> indisponível — credenciais da corretora não configuradas.{' '}
                        <Link to='/settings'>Configurar em Configurações → Corretora</Link>.
                    </div>
                )}

                {isFutures ? (
                    <FuturesAccountCard account={futAccount} />
                ) : (
                <div className='card card-body border-0 shadow table-wrapper table-responsive'>
                    <WalletDonut balances={balances} fiat={fiat} />
                    <table className='table table-hover'>
                        <thead>
                            <tr>
                                <th className='border-gray-200'>Moeda</th>
                                <th className='border-gray-200 text-center'>Disponível</th>
                                <th className='border-gray-200 text-center'>Bloqueado</th>
                                <th className='border-gray-200 text-end'>Total Fiat</th>
                                <th className='border-gray-200 text-end'>Preço Médio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {
                                balances && balances.length
                                    ? balances.filter(b => parseFloat(b.available) > 0 || parseFloat(b.onOrder) > 0)
                                        // Esconde os tokens "LD" do Simple Earn (ex.: LDUSDT) — são a posição de
                                        // Earn espelhada na spot, mostrada no painel de Rendimento, não um ativo.
                                        // Só filtra LD* SEM valor em fiat (preserva tokens reais como LDO/Lido).
                                        .filter(b => !(String(b.symbol).startsWith('LD') && !parseFloat(b.fiatEstimate)))
                                        // Rankeia do maior valor (Total Fiat) pro menor.
                                        .sort((a, b) => (parseFloat(b.fiatEstimate) || 0) - (parseFloat(a.fiatEstimate) || 0))
                                        .map(item => (
                                        <WalletRow key={item.symbol} data={item} />
                                    ))
                                    : <React.Fragment></React.Fragment>
                            }
                        </tbody>
                        <tfoot>
                            <tr>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td className='text-end'>{total}</td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
                )}
                {!isFutures && <CapitalPizza />}
                {!isFutures && <EarnPanel asset='USDT' />}
                <Footer />
            </main>
            <Toast type={notification.type} text={notification.text} />
            <NewOrderModal onSubmit={onOrderSubmit} />
        </React.Fragment>
    )
}

export default Wallet;